import { useAuthStore } from '../stores/auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/** Refresh this many seconds before expiry rather than after a failed call. */
const REFRESH_SKEW_SECONDS = 60;

/** The refresh in flight, so parallel requests do not each start their own. */
let refreshInFlight: Promise<string | null> | null = null;

function accessTokenExpiresWithin(token: string, seconds: number): boolean {
  try {
    const [, payload] = token.split('.');
    if (!payload) return false;
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof exp !== 'number') return false;
    return exp * 1000 - Date.now() < seconds * 1000;
  } catch {
    return false;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const { refreshToken, setAccessToken, logout } = useAuthStore.getState();

    if (!refreshToken) {
      logout();
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/customer/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        logout();
        return null;
      }

      const data = await response.json();
      setAccessToken(data.data.accessToken);
      return data.data.accessToken as string;
    } catch {
      logout();
      return null;
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Download a file the API serves.
 *
 * Not an ordinary link, because an ordinary link carries no Authorization
 * header — it would arrive unauthenticated and be refused. So the file is
 * fetched with the session's own credentials and handed to the browser as a
 * blob, which is also what lets us name it something meaningful.
 */
export async function downloadFile(endpoint: string, filename: string): Promise<void> {
  let { accessToken } = useAuthStore.getState();
  if (accessToken && accessTokenExpiresWithin(accessToken, REFRESH_SKEW_SECONDS)) {
    accessToken = (await refreshAccessToken()) ?? accessToken;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!response.ok) {
    // The API answers errors as JSON even on a download route, so the reason is
    // readable rather than arriving as a corrupt file.
    let message = `Download failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      // Not JSON. The status stands on its own.
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  let { accessToken } = useAuthStore.getState();

  // Ask for a new one before the old one dies, so the customer never sees the
  // failure that a 401-then-retry produces.
  if (accessToken && accessTokenExpiresWithin(accessToken, REFRESH_SKEW_SECONDS)) {
    accessToken = (await refreshAccessToken()) ?? accessToken;
  }
  
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  
  // Only set Content-Type for requests with a body. FormData is the exception:
  // the browser has to set it itself so the multipart boundary is included.
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  
  let response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });
  
  // Handle token refresh on 401
  if (response.status === 401 && accessToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });
    } else {
      window.location.href = import.meta.env.BASE_URL + 'login';
      throw new Error('Session expired');
    }
  }
  
  if (response.status === 401) {
    useAuthStore.getState().logout();
    window.location.href = import.meta.env.BASE_URL + 'login';
    throw new Error('Session expired');
  }
  
  const data = await response.json();
  
  if (!response.ok) {
    // The status rides along: callers need to tell "this is switched off" from
    // "this broke", and the message alone cannot carry that.
    const error = new Error(data.error || 'An error occurred') as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  
  return data;
}

// Customer Auth API
export const customerAuthApi = {
  // Password reset. Unauthenticated by necessity — the caller cannot sign in.
  forgotPassword: (email: string) =>
    apiFetch<{ message: string }>('/customer/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  validateResetToken: (token: string) =>
    apiFetch<{ valid: boolean }>(`/customer/auth/reset-password/validate?token=${encodeURIComponent(token)}`),

  resetPassword: (token: string, password: string) =>
    apiFetch<{ message: string; sessionsRevoked: number }>('/customer/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  login: (email: string, password: string) =>
    apiFetch<{
      accessToken: string;
      refreshToken: string;
      customer: { id: string; email: string; firstName?: string; lastName?: string };
    }>('/customer/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  
  me: () => apiFetch('/customer/auth/me'),
  
  logout: (refreshToken: string) =>
    apiFetch('/customer/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),
};

// Customer Products API
export const customerProductsApi = {
  list: (params?: { page?: number; limit?: number; search?: string; expired?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.search) sp.set('search', params.search);
    if (params?.expired) sp.set('expired', 'true');
    return apiFetch(`/customer/services?${sp}`);
  },
  
  get: (id: string) => apiFetch(`/customer/services/${id}`),
  
  /** Counts and the next few renewals, for the dashboard. */
  summary: () => apiFetch(`/customer/services/summary`),
  
  /** Live disk, bandwidth and mailbox usage read from the server. */
  usage: (id: string) => apiFetch(`/customer/services/${id}/usage`),
  
  getSso: (id: string) => apiFetch(`/customer/services/${id}/sso`),

  /** Raise the next period as its own order and invoice, to be paid. */
  renew: (id: string) => apiFetch(`/customer/services/${id}/renew`, { method: 'POST' }),
};

// Customer Invoices API
export const customerInvoicesApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    apiFetch(`/customer/invoices?${new URLSearchParams(params as any).toString()}`),
  
  get: (id: string) => apiFetch(`/customer/invoices/${id}`),
  
  createPaymentIntent: (id: string) =>
    apiFetch<{ clientSecret: string; amount: number; currency: string }>(
      `/customer/invoices/${id}/payment-intent`,
      { method: 'POST' }
    ),
};

// Customer Payments API
export const customerPaymentsApi = {
  list: (params?: { page?: number; limit?: number }) =>
    apiFetch(`/customer/payments?${new URLSearchParams(params as any).toString()}`),
  
  getMethods: () => apiFetch('/customer/payments/methods'),
  
  createSetupIntent: () =>
    apiFetch<{ clientSecret: string }>('/customer/payments/methods/setup-intent', {
      method: 'POST',
    }),
  
  setDefaultMethod: (methodId: string) =>
    apiFetch(`/customer/payments/methods/${methodId}/default`, { method: 'POST' }),
  
  removeMethod: (methodId: string) =>
    apiFetch(`/customer/payments/methods/${methodId}`, { method: 'DELETE' }),

  // Payment methods enabled by the provider (cards, SPEI transfer, …)
  getProviders: () => apiFetch<PaymentProvider[]>('/customer/payments/providers'),

  // Evidence of an out-of-band transfer. Sent as FormData when the customer
  // attaches a receipt, JSON when they paste a link to one.
  submitInvoiceProof: (invoiceId: string, body: FormData | ProofPayload) =>
    apiFetch(`/customer/payments/invoices/${invoiceId}/proof`, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  listProofs: () => apiFetch<PaymentProof[]>('/customer/payments/proofs'),
};

export interface BankDetails {
  accountName?: string;
  bankName?: string;
  accountNumber?: string;
  clabe?: string;
  iban?: string;
  swift?: string;
  referenceFormat?: string;
}

export interface PaymentProvider {
  provider: 'STRIPE' | 'PAYPAL' | 'MERCADOPAGO' | 'BANK_WIRE';
  displayName: string;
  supportsProofUpload: boolean;
  supportsAutoConfirm: boolean;
  bankDetails?: BankDetails;
}

export interface ProofPayload {
  amount?: number;
  currency?: string;
  reference?: string;
  proofUrl?: string;
  notes?: string;
}

export interface PaymentProof {
  id: string;
  invoiceId: string | null;
  amount: number;
  currency: string;
  reference: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNotes: string | null;
  evidenceType: 'FILE' | 'URL';
  fileDownloadUrl: string | null;
  proofUrl: string | null;
  createdAt: string;
  invoice?: { id: string; number: string; total: number; status: string };
}

// Customer Profile API
export const customerProfileApi = {
  get: () => apiFetch('/customer/profile'),
  
  update: (data: {
    firstName?: string;
    lastName?: string;
    company?: string;
    phone?: string;
    address?: { street?: string; city?: string; state?: string; country?: string; zip?: string };
    taxId?: string;
  }) =>
    apiFetch('/customer/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch('/customer/profile/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// Customer Tickets API
export const customerTicketsApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    apiFetch(`/customer/tickets?${new URLSearchParams(params as any).toString()}`),
  
  get: (id: string) => apiFetch(`/customer/tickets/${id}`),
  
  create: (data: {
    subject: string;
    message: string;
    priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
    department?: string;
    productId?: string;
  }) =>
    apiFetch('/customer/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  reply: (id: string, message: string) =>
    apiFetch(`/customer/tickets/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  
  close: (id: string) =>
    apiFetch(`/customer/tickets/${id}/close`, { method: 'POST' }),
};

// Customer Orders API
export const customerOrdersApi = {
  listPackages: () => apiFetch('/customer/orders/packages'),
  
  create: (data: { packageId: string; domain: string; billingCycle: string }) =>
    apiFetch('/customer/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  get: (id: string) => apiFetch(`/customer/orders/${id}`),
};
